import React, {useMemo} from 'react';
import clsx from 'clsx';
import {
	faClone as CloneIcon,
	faPencil as PencilIcon,
	faTimes as TimesIcon,
} from '@netx/core-icons';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import Selection from '@netx/core-attribute-set-selection/components/SetSelector';
// import {
// 	Selection as SetSelection,
// 	SelectionProps as SetSelectionProps,
// } from '@netx/core-asset/components/Attributes/Sets';
import {Button} from '@netx/core-components/Button';
import {CountChip} from '@netx/core-components/CountChip';
import {IconButton} from '@netx/core-components/IconButton';
import {type Theme} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';
import {ChangesChip} from '..';
import {
	useChangeStore,
	useGridStore,
	useProgressStore,
	useSelectionStore,
} from '../../store';

const styles = (theme: Theme) =>
	createStyles({
		root: {
			color: theme.palette.text.primary,
			padding: theme.spacing(1.5, 2, 1, 2),
			display: 'flex',
			alignItems: 'center',
		},
		title: {
			flex: '0 1 auto',
			display: 'flex',
			alignItems: 'center',
		},
		closer: {
			color: theme.palette.text.primary,
			flex: '0 0 auto',
		},
		counts: {
			display: 'flex',
			flex: '1 1 auto',
			marginRight: theme.spacing(1),
			'& > * + *': {
				marginLeft: theme.spacing(1),
			},
		},
		actions: {
			display: 'flex',
			flex: '0 0 auto',
			marginRight: theme.spacing(1),
			'& > * + *': {
				marginLeft: theme.spacing(1),
			},
		},
		controls: {
			display: 'flex',
			flex: '1 0 auto',
			alignItems: 'center',
		},
		working: {
			pointerEvents: 'none',
		},
		setSelection: {
			flex: '1 1 auto',
			maxWidth: 300,
			minWidth: 220,
			padding: theme.spacing(0, 2, 0, 0),
		},
	});

export interface MainHeaderProps {
	onClone?: () => void;
	onClose?: () => void;
	onEdit?: () => void;
	// onSetChange: SelectionProps['onChange'];
}

const MainHeader: React.FC<WithStyles<typeof styles> & MainHeaderProps> =
	React.memo(
		({
			classes,

			onClone,
			onClose,
			onEdit,
			// onSetChange,
		}) => {
			const selection = useSelectionStore();
			const grid = useGridStore();
			const changes = useChangeStore();
			const progress = useProgressStore();

			const hasChanges = !!changes.state.count;
			const hasSelection = !!selection.state.count;

			const working =
				progress.state.working || grid.state.loading || grid.state.saving;

			return useMemo(
				() => (
					<div
						className={clsx(classes.root, {
							[classes.working]: working,
						})}
					>
						<div className={classes.controls}>
							<Selection
								className={classes.setSelection}
								// confirm={hasChanges}
								// defaultSelection={['all']}
								// onChange={onSetChange}
								// userPrefKey="attribute.attributeSets.selected.tagPanelGrid"
							/>

							<div className={classes.counts}>
								<CountChip
									count={selection.state.count}
									label={i18n.t('i18n.selected')}
									meshAvatar
									onClear={selection.reset}
									color="info"
								/>
								<ChangesChip />
							</div>
						</div>

						<div className={classes.actions}>
							{onEdit ? (
								<Button
									color="primary"
									disabled={!hasSelection}
									onClick={onEdit}
									startIcon={PencilIcon}
									variant="outlined"
								>
									{i18n.t('i18n.edit')}
								</Button>
							) : null}

							{onClone ? (
								<Button
									color="primary"
									disabled={selection.state.inSingleRow === false}
									onClick={onClone}
									startIcon={CloneIcon}
									variant="outlined"
								>
									{i18n.t('i18n.clone')}
								</Button>
							) : null}

							<Button
								color="primary"
								disabled={!hasChanges || working}
								onClick={grid.save}
								variant="contained"
							>
								{i18n.t('i18n.save')}
							</Button>
						</div>

						{onClose ? (
							<div className={classes.closer}>
								<IconButton
									icon={TimesIcon}
									onClick={onClose}
									label={i18n.t('i18n.close')}
								/>
							</div>
						) : null}
					</div>
				),
				[
					classes,

					onClose,
					onEdit,

					hasChanges,
					hasSelection,
					working,

					changes.state.count,
					grid.resetAllChanges,
					grid.save,
					selection.state.count,
					selection.state.inSingleRow,
					selection.reset,
				],
			);
		},
	);

export default withStyles(styles)(MainHeader);
